import { computed, effect, inject, Injectable, signal } from '@angular/core'
import { Category } from '../models/auth.model'
import { PermissionService } from './permission.service'
import { CertificateCategory } from '@app/shared/models/certificate-category.model'

const STORAGE_KEY = 'albert.activeCategory'

@Injectable({ providedIn: 'root' })
export class CategoryService {
  public readonly categories: { label: string, value: Category }[] = [
    { label: 'Authority', value: Category.AVIATIONAUTH },
    { label: 'Contractual', value: Category.AVIATIONCONTR },
    { label: 'Lease', value: Category.AVIATIONLEASE },
    { label: 'Liability', value: Category.INDUSTRYLIAB },
    { label: 'LSG', value: Category.AVIATIONLSG },
    { label: 'Property', value: Category.INDUSTRYPROP }
  ]
  public readonly activeCategory = signal<Category | null>(null)

  public readonly activeCertificateCategory = computed(() => {
    const category = this.activeCategory()
    if (category === null) return null

    switch (category) {
      case Category.AVIATIONAUTH:
        return CertificateCategory.AUTHORITIES
      case Category.AVIATIONCONTR:
        return CertificateCategory.CONTRACTUAL
      case Category.AVIATIONLEASE:
        return CertificateCategory.LEASE
      case Category.AVIATIONLSG:
        return CertificateCategory.LSG
      case Category.INDUSTRYLIAB:
        return CertificateCategory.LIABILITY
      case Category.INDUSTRYPROP:
        return CertificateCategory.PROPERTY
      default:
        return null
    }
  })

  private readonly permissionService = inject(PermissionService)
  public readonly availableCategories = computed(() => {
    return this.categories.filter(cat =>
      this.permissionService.canView?.(cat.value) ||
      this.permissionService.canEdit?.(cat.value)
    )
  })

  constructor() {
    effect(() => {
      const available = this.availableCategories()
      const firstAvailable = available[0]
      if (firstAvailable && (this.activeCategory() === null)) {
        this.activeCategory.set(this.readStoredCategory(available) ?? firstAvailable.value)
      }
    })
  }

  public setCategory(category: Category): void {
    this.activeCategory.set(category)
    this.storeCategory(category)
  }

  private readStoredCategory(available: { value: Category }[]): Category | null {
    let stored: string | null = null
    try {
      stored = sessionStorage.getItem(STORAGE_KEY)
    } catch {
      // Storage can be unavailable (private mode, blocked cookies); falling back to the first
      // category is fine, failing to start is not.
    }
    return available.some(cat => cat.value === stored) ? stored as Category : null
  }

  private storeCategory(category: Category): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, category)
    } catch {
      // See readStoredCategory().
    }
  }
}
